import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import FileSaver from 'file-saver';
import { ApplicationState } from '../../reducers';
import {
  fetchImport,
  fetchImportLogDetailsJSON,
  fetchImportLogs,
  fetchImportLogsDetailsExcel,
} from '../../actions/brand/import_export/fetch';
import ImportExportLogs from '../../components/body/import_export/ImportExportLogs';
import { importLogMapper } from '../../selectors/importExportLogsSelector';
import { getSelectedBrandCode } from '../../selectors/brand/brandSelector';
import { AsyncDispatch } from '../../../types/global';
import { isManufacturer } from '../../utils/UserUtils';
import { intercomEvent } from '../../utils/IntercomUtils';
import { fetchExportOptions } from '../../actions/brand/export/fetch';
import { openImportNotification } from '../../actions/importer';
import { getPageLimit } from '../../utils/Utils';
import { useAppDispatch } from '../../store/ReduxStore';

const ImportLogsContainer: React.FC = () => {
  const dispatch: AsyncDispatch = useAppDispatch();

  const {
    brandId,
    importLogs,
    fetchingImportLogs,
    fetchingImportErrors,
    importErrorLogDetail,
    fileExportOptionsBrandIds,
    user,
    userNameList,
    brandCode,
    importSources,
    jobStatuses,
  } = useSelector(
    (state: ApplicationState) => ({
      brandId: state.parent.brands.selectedBrandId,
      importLogs: importLogMapper(state),
      fetchingImportLogs: state.brand.importExport.fetchingImportLogs,
      fetchingImportErrors: state.brand.importExport.fetchingImportErrors,
      importErrorLogDetail: state.brand.importExport.importErrorLogDetail,
      fileExportOptionsBrandIds: state.brand.brandExport.fileExportOptionsBrandIds,
      user: state.user.user,
      userNameList: state.user.userNameList,
      brandCode: getSelectedBrandCode(state),
      importSources: state.resources.data.global.import_sources,
      jobStatuses: state.resources.data.global.job_statuses,
    }),
    shallowEqual
  );

  const manufacturer = isManufacturer(user!);

  React.useEffect(() => {
    intercomEvent('viewed-import-export', {
      location: 'viewed-import-reports',
      brand_code: brandCode!,
    });
  }, [brandCode]);

  const [pageSize] = React.useState<number>(getPageLimit());
  const [selectedTypeFilterIds, setSelectedTypeFilterIds] = React.useState<number[]>([]);
  const [selectedStatusFilterIds, setSelectedStatusFilterIds] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (manufacturer && (!fileExportOptionsBrandIds || fileExportOptionsBrandIds[0] !== brandId))
      dispatch(fetchExportOptions('export', brandId));
  }, [brandId, dispatch, fileExportOptionsBrandIds, manufacturer]);

  React.useEffect(() => {
    dispatch(fetchImportLogs({ brandId, limit: pageSize }));
  }, [brandId, dispatch, pageSize]);

  const saveImportLogsDetailsFile = ({ errorLogFile }: { errorLogFile: any }) => {
    const blob = new Blob([errorLogFile], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    FileSaver.saveAs(blob, 'paramount_import_errors.xlsx');
  };

  const handleErrorDownload = (logId: number) =>
    dispatch(fetchImportLogsDetailsExcel(logId)).then(response =>
      saveImportLogsDetailsFile({ errorLogFile: response.action.payload.data })
    );

  const handleImportFileDownload = (logId: number) =>
    // @ts-ignore
    dispatch(fetchImport(logId));

  const handleFetchImportLogDetailsJSON = (logId: number) =>
    dispatch(fetchImportLogDetailsJSON(logId));

  const handleOpenNotification = (logId: number) => dispatch(openImportNotification(logId));

  const handleSourceFilter = (sourceIds: number[]) => {
    if (JSON.stringify(sourceIds) !== JSON.stringify(selectedTypeFilterIds))
      dispatch(
        fetchImportLogs({
          brandId,
          sourceIds,
          statusIds: selectedStatusFilterIds,
          limit: pageSize,
        })
      );
    setSelectedTypeFilterIds(sourceIds);
  };

  const handleStatusFilter = (statusIds: number[]) => {
    if (JSON.stringify(statusIds) !== JSON.stringify(selectedStatusFilterIds))
      dispatch(
        fetchImportLogs({
          brandId,
          sourceIds: selectedTypeFilterIds,
          statusIds,
          limit: pageSize,
        })
      );
    setSelectedStatusFilterIds(statusIds);
  };

  const getImportLogs = (page: number) => {
    return dispatch(
      fetchImportLogs({
        brandId,
        sourceIds: selectedTypeFilterIds,
        statusIds: selectedStatusFilterIds,
        limit: pageSize,
        page,
      })
    );
  };

  return (
    <ImportExportLogs
      logs={importLogs}
      fetchingLogs={fetchingImportLogs}
      fetchingErrors={fetchingImportErrors}
      errorLogDetails={importErrorLogDetail}
      handleErrorDownload={handleErrorDownload}
      handleFileDownload={handleImportFileDownload}
      handleFetchLogDetailsJSON={handleFetchImportLogDetailsJSON}
      handleOpenNotification={handleOpenNotification}
      fileTypeValues={importSources}
      jobStatuses={jobStatuses}
      pageSize={pageSize}
      selectedTypeFilterIds={selectedTypeFilterIds}
      selectedStatusFilterIds={selectedStatusFilterIds}
      handleFilterByType={handleSourceFilter}
      handleFilterByStatus={handleStatusFilter}
      fetchLogs={getImportLogs}
    />
  );
};

export default ImportLogsContainer;
